import { Controller } from "@hotwired/stimulus"

// Controls the activity feed filter buttons on admin/activity/index.
// Toggling a filter button shows/hides matching activity items.
//
// Usage:
//   <div data-controller="activity-filter">
//     <button data-action="click->activity-filter#toggle"
//             data-activity-filter-type-param="quest"
//             data-activity-filter-target="button"
//             data-filter-type="quest">Quests</button>
//     ...
//     <div data-activity-filter-target="emptyState">No items</div>
//     <ul data-activity-filter-target="list">
//       <li data-activity-filter-target="item" data-activity-type="quest">...</li>
//     </ul>
//   </div>
//
export default class extends Controller {
  static targets = ["button", "item", "emptyState", "list"]

  // Button style config keyed by filter type
  static values = {
    active: { type: Array, default: ["quest", "task"] }
  }

  // Base classes shared by every filter button
  BASE_CLASSES = "inline-flex items-center px-3 py-1.5 border-2 text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors"

  STYLES = {
    quest:   { active: "bg-blue-600 text-white border-blue-600 hover:bg-blue-700 focus:ring-blue-500",
               inactive: "bg-white text-gray-600 border-gray-300 hover:bg-gray-50 focus:ring-blue-500" },
    task:    { active: "bg-green-600 text-white border-green-600 hover:bg-green-700 focus:ring-green-500",
               inactive: "bg-white text-gray-600 border-gray-300 hover:bg-gray-50 focus:ring-green-500" },
    mention: { active: "bg-purple-600 text-white border-purple-600 hover:bg-purple-700 focus:ring-purple-500",
               inactive: "bg-white text-gray-600 border-gray-300 hover:bg-gray-50 focus:ring-purple-500" },
    comment: { active: "bg-gray-600 text-white border-gray-600 hover:bg-gray-700 focus:ring-gray-500",
               inactive: "bg-white text-gray-600 border-gray-300 hover:bg-gray-50 focus:ring-gray-500" }
  }

  connect() {
    this.activeFilters = new Set(this.activeValue)
    this._applyVisibility()
  }

  toggle(event) {
    const type = event.params.type

    if (this.activeFilters.has(type)) {
      this.activeFilters.delete(type)
    } else {
      this.activeFilters.add(type)
    }

    this._styleButton(event.currentTarget, type)
    this._applyVisibility()
  }

  // Private

  _styleButton(button, type) {
    const style = this.STYLES[type]
    if (!style) return

    const variant = this.activeFilters.has(type) ? "active" : "inactive"
    button.className = `${this.BASE_CLASSES} ${style[variant]} opacity-100`
  }

  _applyVisibility() {
    let visibleCount = 0

    this.itemTargets.forEach(item => {
      const itemType = item.dataset.activityType
      if (this.activeFilters.has(itemType)) {
        item.style.display = ""
        visibleCount++
      } else {
        item.style.display = "none"
      }
    })

    const showEmpty = this.activeFilters.size === 0 || visibleCount === 0

    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.style.display = showEmpty ? "" : "none"
    }

    if (this.hasListTarget) {
      this.listTarget.style.display = showEmpty ? "none" : ""
    }
  }
}
